/**
 * One farmer, everything known: the record (farms, areas, segment, last crop),
 * where they stand on WhatsApp onboarding, the controls a field officer uses
 * on the spot (phone, invite link, QR, notes), and the prebuilt field history
 * for farms in the programme block - the same parameters the AI Bhoomi farm
 * map shows, plus the onboarding lane.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Holder, OnboardingEvent, OnboardingRow, Stage } from '@shared/model';
import { SEGMENT_COLOR, SEGMENT_LABEL, STAGES, STAGE_COLOR, STAGE_LABEL, prettyPhone, stageIndex } from '@shared/model';
import { apiGet, apiPost, type Loadable } from '../api/client';
import { useRoster } from '../state/RosterContext';
import { useSession } from '../state/SessionContext';
import { StagePill } from './StagePill';
import { BlockRecordPanel } from './panels/BlockRecordPanel';
import { CropPicker, parseCrops } from './CropPicker';
import { AdvisoryPanel } from './AdvisoryPanel';

interface LinkData {
  number: string;
  link: string;
  qr: string;
  message: string;
  code: string;
  attributed: boolean;
}

export const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STAGE_AT: Partial<Record<Stage, keyof OnboardingRow>> = {
  phone: 'phone_at', invited: 'invited_at', joined: 'joined_at', pin: 'pin_at', boundary: 'boundary_at', activated: 'activated_at',
};

const acres = (n: number): string => n.toFixed(n < 10 ? 2 : 1);

/** The farmer's one-acre plot in the programme block, when the card is opened from there. */
export interface PlotRef { holder_id: string; rank: number; khasras: string[]; pin: [number, number]; acres: number; clipped: boolean }

export function FarmerCard({ holder, plot, onClose, onShowFarm, onZones }: { holder: Holder; plot?: PlotRef; onClose?: () => void; onShowFarm?: (pid: string, pin: [number, number]) => void; onZones?: (fc: GeoJSON.FeatureCollection | null, pid: string | null) => void }): JSX.Element {
  const { rows, farmIndex, staff, whatsapp, patch, reload } = useRoster();
  const { account } = useSession();
  const row = rows.get(holder.id) ?? null;
  const stage: Stage = row?.stage ?? 'target';

  const [phone, setPhone] = useState(row?.phone ?? '');
  const [note, setNote] = useState(row?.notes ?? '');
  const [assignee, setAssignee] = useState(row?.assigned_to ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [events, setEvents] = useState<OnboardingEvent[]>([]);
  const [link, setLink] = useState<Loadable<LinkData>>({ status: 'loading' });
  const [showQr, setShowQr] = useState(false);
  const [direct, setDirect] = useState('');
  const blockFarms = useMemo(() => holder.farms.filter((f) => f.in_block), [holder]);
  const cropList = useMemo(() => parseCrops(row?.crops), [row?.crops]);
  const [historyPid, setHistoryPid] = useState<string | null>(blockFarms[0]?.pid ?? null);

  useEffect(() => {
    setPhone(row?.phone ?? '');
    setNote(row?.notes ?? '');
    setAssignee(row?.assigned_to ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holder.id, row?.phone, row?.notes, row?.assigned_to]);

  useEffect(() => {
    let live = true;
    setMsg(null);
    setShowQr(false);
    setHistoryPid(blockFarms[0]?.pid ?? null);
    void apiGet<{ row: OnboardingRow | null; events: OnboardingEvent[] }>(`/api/onboarding/${encodeURIComponent(holder.id)}`).then((r) => {
      if (live && r.status === 'ready' && r.data) setEvents(r.data.events);
    });
    setLink({ status: 'loading' });
    void apiPost<LinkData>('/api/whatsapp/link', { holder_id: holder.id }).then((r) => live && setLink(r));
    return () => {
      live = false;
    };
  }, [holder.id, blockFarms]);

  const save = async (body: Record<string, unknown>, okText: string): Promise<void> => {
    setBusy(true);
    setMsg(null);
    const r = await patch(holder.id, body);
    setBusy(false);
    if (typeof r === 'string') setMsg({ ok: false, text: r });
    else {
      setEvents(r.events);
      setMsg({ ok: true, text: okText });
    }
  };

  const openWhatsApp = (): void => {
    void apiPost('/api/whatsapp/link', { holder_id: holder.id, mark: true }).then(() => reload());
  };

  const copy = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setMsg({ ok: true, text: 'Copied.' });
    } catch {
      setMsg({ ok: false, text: 'Clipboard refused; the text is on screen.' });
    }
  };

  const pinFarm = row?.pin_pid ? farmIndex.get(row.pin_pid) : null;
  const boundary = useMemo(() => {
    if (!row?.boundary_json) return null;
    try {
      return JSON.parse(row.boundary_json) as [number, number][];
    } catch {
      return null;
    }
  }, [row?.boundary_json]);

  const stageButtons: { stage: Stage; label: string }[] = [
    { stage: 'phone', label: 'Phone captured' },
    { stage: 'invited', label: 'Invited' },
    { stage: 'pin', label: 'Pin shared' },
    { stage: 'boundary', label: 'Boundary marked' },
  ];

  return (
    <div className="fc">
      <div className="fc__head">
        <div className="fc__row" style={{ justifyContent: 'space-between' }}>
          <h2>{holder.name}</h2>
          {onClose ? <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">×</button> : null}
        </div>
        <p>
          {holder.relation ? `${holder.relation} · ` : ''}{holder.village} · {holder.farm_count} khasra{holder.farm_count === 1 ? '' : 's'} · {acres(holder.acres)} acres
        </p>
        <div className="fc__row" style={{ marginTop: 8 }}>
          <StagePill stage={stage} />
          {holder.segment ? (
            <span className="stage-pill" style={{ borderColor: SEGMENT_COLOR[holder.segment], color: 'var(--text-secondary)' }}>
              <i style={{ background: SEGMENT_COLOR[holder.segment] }} />
              {SEGMENT_LABEL[holder.segment]}{holder.segment_source === 'shared' ? ' (shared khasra)' : ''}
            </span>
          ) : (
            <span className="stage-pill muted"><i style={{ background: '#6b7280' }} />holder not matched to a holding</span>
          )}
          {holder.in_block ? <span className="stage-pill" style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}><i style={{ background: 'var(--accent)' }} />1,000-acre block</span> : null}
        </div>
      </div>

      <section>
        <h4>On the record</h4>
        <table className="vtable">
          <thead>
            <tr><th>Khasra</th><th className="num">Acres</th><th>Pin inside the farm</th><th></th></tr>
          </thead>
          <tbody>
            {holder.farms.map((f) => (
              <tr key={f.pid}>
                <td>
                  {f.khasra}
                  {f.in_block ? <span className="muted"> · block</span> : null}
                  {f.shared ? <div className="muted" style={{ fontSize: 11.5 }}>with {f.co_owners.slice(0, 3).join(', ')}{f.co_owners.length > 3 ? ` +${f.co_owners.length - 3}` : ''}</div> : null}
                </td>
                <td className="num">{acres(f.acres)}</td>
                <td className="mono">{f.pin[0].toFixed(5)}, {f.pin[1].toFixed(5)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button type="button" className="btn-ghost" style={{ padding: '3px 7px', fontSize: 11.5 }} onClick={() => void copy(`${f.pin[0].toFixed(6)}, ${f.pin[1].toFixed(6)}`)}>Copy</button>{' '}
                  <a className="btn-ghost" style={{ padding: '3px 7px', fontSize: 11.5, textDecoration: 'none' }} href={`https://maps.google.com/?q=${f.pin[0]},${f.pin[1]}`} target="_blank" rel="noreferrer">Maps</a>
                  {onShowFarm ? <>{' '}<button type="button" className="btn-ghost" style={{ padding: '3px 7px', fontSize: 11.5 }} onClick={() => onShowFarm(f.pid, f.pin)}>Show</button></> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <dl style={{ marginTop: 10 }}>
          <dt>Holder&rsquo;s total</dt>
          <dd>{holder.holding_acres != null ? `${acres(holder.holding_acres)} acres across ${holder.holding_parcels ?? holder.farm_count} parcel${(holder.holding_parcels ?? 1) > 1 ? 's' : ''} (record)` : `${acres(holder.acres)} acres on ${holder.farm_count} khasra${holder.farm_count === 1 ? '' : 's'}`}</dd>
          <dt>Last season</dt>
          <dd>{holder.crop_last_season ?? <em className="muted">not read yet — the season scan has not covered this village</em>}</dd>
        </dl>
      </section>

      <section>
        <h4>Crops this farmer grows</h4>
        <CropPicker value={cropList} busy={busy} onSave={(crops) => void save({ crops }, 'Crops saved.')} />
      </section>

      {plot ? (
        <section>
          <h4>Programme block · farm {plot.rank.toLocaleString('en-IN')}</h4>
          <dl>
            <dt>Plot</dt>
            <dd>{plot.acres.toFixed(2)} acre {plot.clipped ? 'cut from' : '='} khasra {plot.khasras.join(' + ')}</dd>
            <dt>Plot pin</dt>
            <dd className="mono">{plot.pin[0].toFixed(5)}, {plot.pin[1].toFixed(5)}{' '}
              <a className="btn-ghost" style={{ padding: '2px 7px', fontSize: 11.5, textDecoration: 'none' }} href={`https://maps.google.com/?q=${plot.pin[0]},${plot.pin[1]}`} target="_blank" rel="noreferrer">Maps</a>
            </dd>
          </dl>
        </section>
      ) : null}

      <AdvisoryPanel holder={holder} crops={cropList} plot={plot} onZones={onZones} onEvents={setEvents} />

      <section>
        <h4>WhatsApp onboarding</h4>
        <ul className="fc__timeline">
          {STAGES.filter((s) => s !== 'target').map((s) => {
            const done = stageIndex(s) <= stageIndex(stage);
            const atKey = STAGE_AT[s];
            const at = atKey && row ? (row[atKey] as string | null) : null;
            return (
              <li key={s} className={done ? 'is-done' : 'is-todo'}>
                <i style={done ? { background: STAGE_COLOR[s] } : undefined} />
                <span>{STAGE_LABEL[s]}</span>
                <span className="when">{done ? fmtDate(at) : ''}</span>
              </li>
            );
          })}
        </ul>
        {row?.bot_state ? <p className="fc__note">Bot state: <code>{row.bot_state}</code>{row.bot_name ? ` · WhatsApp name “${row.bot_name}”` : ''}{row.messages ? ` · ${row.messages} messages, last ${fmtDate(row.last_message_at)}` : ''}</p> : null}
        {row?.pin_lat != null && row.pin_lon != null ? (
          row.pin_pid && row.pin_inside_own ? (
            <p className="fc__note fc__note--ok">✓ Pin {row.pin_lat.toFixed(5)}, {row.pin_lon.toFixed(5)} is inside this holder&rsquo;s own khasra {pinFarm?.khasra ?? row.pin_pid}.</p>
          ) : row.pin_pid ? (
            <p className="fc__note warn">⚠ Pin {row.pin_lat.toFixed(5)}, {row.pin_lon.toFixed(5)} is inside khasra {pinFarm?.khasra ?? row.pin_pid}{pinFarm ? `, ${pinFarm.village}` : ''} — on the record that khasra is held by someone else. Confirm who sent it.</p>
          ) : (
            <p className="fc__note fc__note--bad">✗ Pin {row.pin_lat.toFixed(5)}, {row.pin_lon.toFixed(5)} falls outside every farm in the record{row.pin_distance_m != null ? ` (${row.pin_distance_m} m from the nearest)` : ''}. Ask the farmer to share the location standing in the field.</p>
          )
        ) : (
          <p className="fc__note">No WhatsApp pin yet.</p>
        )}
        {boundary ? <p className="fc__note">Boundary: {boundary.length} corners, {row?.boundary_acres != null ? `${acres(row.boundary_acres)} acres, ` : ''}{row?.boundary_source}.</p> : null}
        {row?.phone ? <p className="fc__note">Phone <strong>{prettyPhone(row.phone)}</strong>{row.wa_id && row.wa_id !== row.phone ? ` · WhatsApp ${prettyPhone(row.wa_id)}` : ''}{row.assigned_to ? ` · assigned to ${row.assigned_to}` : ''}</p> : null}
      </section>

      <section>
        <h4>Field officer</h4>
        <div className="fc__controls">
          <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+ ]/g, ''))} placeholder="Farmer's mobile, e.g. 98xxxxxxxx" inputMode="tel" />
          <button type="button" className="btn-primary" disabled={busy || phone.replace(/\D/g, '').length < 10} onClick={() => void save({ phone }, 'Phone saved.')}>Save phone</button>
          <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">Unassigned</option>
            {staff.map((s) => <option key={s.email} value={s.email}>{s.name} ({s.email})</option>)}
            {account && !staff.some((s) => s.email === account.email) ? <option value={account.email}>{account.name} ({account.email})</option> : null}
          </select>
          <button type="button" className="btn-line" disabled={busy || (assignee || '') === (row?.assigned_to ?? '')} onClick={() => void save({ assigned_to: assignee }, 'Assignee saved.')}>Assign</button>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notes from the visit: who was met, which khasra they farm, when to come back…" />
          <span />
          <button type="button" className="btn-line" disabled={busy || note === (row?.notes ?? '')} onClick={() => void save({ notes: note }, 'Note saved.')}>Save note</button>
        </div>
        <div className="fc__buttons">
          {stageButtons.map((b) => (
            <button key={b.stage} type="button" className="btn-ghost" disabled={busy || stageIndex(b.stage) <= stageIndex(stage)} title={`Record that this farmer reached “${b.label}”`} onClick={() => void save({ stage: b.stage }, `${b.label} recorded.`)}>
              {b.label}
            </button>
          ))}
          {account?.admin && row ? (
            <button type="button" className="btn-ghost" disabled={busy} title="Admin: put this farmer back to the record only" onClick={() => void save({ stage: 'target', force: true, note: 'reset by admin' }, 'Reset.')}>Reset</button>
          ) : null}
        </div>
        {msg ? <p className={`fc__note ${msg.ok ? 'fc__note--ok' : 'fc__note--bad'}`}>{msg.text}</p> : null}
        {events.length ? (
          <ul className="fc__events">
            {events.slice(0, 8).map((e) => (
              <li key={e.id}><span>{fmtDate(e.at)} · {e.by.split('@')[0]}</span><span>{e.detail}</span></li>
            ))}
          </ul>
        ) : null}
      </section>

      <section>
        <h4>WhatsApp link to the AI Bhoomi bot</h4>
        {link.status === 'ready' ? (
          <>
            <p className="fc__note">Bot number <strong>{prettyPhone(link.data.number)}</strong> · farmer code <code>{link.data.code}</code>{link.data.attributed ? ' · official deep link (origin recorded by WhatsApp)' : ' · plain link; the greeting carries the code'}</p>
            <div className="fc__buttons">
              <a className="btn-primary" href={link.data.link} target="_blank" rel="noreferrer" onClick={openWhatsApp}>Open in WhatsApp</a>
              <button type="button" className="btn-line" onClick={() => setShowQr((v) => !v)}>{showQr ? 'Hide QR' : 'Show QR'}</button>
              <button type="button" className="btn-line" onClick={() => void copy(link.data.link)}>Copy link</button>
              <button type="button" className="btn-line" onClick={() => void copy(link.data.message)}>Copy greeting</button>
            </div>
            {showQr ? <img className="fc__qr" src={link.data.qr} alt="QR code opening the farmer's WhatsApp conversation with the bot" /> : null}
            <div className="fc__msg">{link.data.message}</div>
            {whatsapp?.direct ? (
              <div className="fc__controls" style={{ marginTop: 10 }}>
                <textarea value={direct} onChange={(e) => setDirect(e.target.value)} placeholder={row?.phone ? `Send a message to ${prettyPhone(row.phone)}…` : 'Save the farmer’s phone first to send directly.'} />
                <span />
                <button type="button" className="btn-line" disabled={busy || !row?.phone || !direct.trim()} onClick={() => {
                  setBusy(true);
                  void apiPost('/api/whatsapp/send', { to: row?.phone, text: direct, holder_id: holder.id }).then((r) => {
                    setBusy(false);
                    setMsg(r.status === 'ready' ? { ok: true, text: 'Sent.' } : { ok: false, text: r.status === 'error' || r.status === 'not_configured' ? r.message : 'Not sent.' });
                    if (r.status === 'ready') setDirect('');
                  });
                }}>Send directly</button>
              </div>
            ) : null}
          </>
        ) : link.status === 'loading' ? (
          <p className="fc__note">Preparing the link…</p>
        ) : (
          <p className="fc__note fc__note--bad">{link.status === 'error' || link.status === 'not_configured' ? link.message : 'No link.'}</p>
        )}
      </section>

      {blockFarms.length ? (
        <section>
          <h4>Field history (programme block)</h4>
          {blockFarms.length > 1 ? (
            <select className="btn-ghost" style={{ marginBottom: 8 }} value={historyPid ?? ''} onChange={(e) => setHistoryPid(e.target.value)}>
              {blockFarms.map((f) => <option key={f.pid} value={f.pid}>Khasra {f.khasra} · {acres(f.acres)} ac</option>)}
            </select>
          ) : null}
          {historyPid ? <BlockRecordPanel pid={historyPid} /> : null}
        </section>
      ) : null}
    </div>
  );
}
